import React from 'react'
import PropTypes from 'prop-types'
import { Button, Form, Icon, Modal, Rating } from 'semantic-ui-react'

/**
 * Presentational modal for adding a skill with a star rating.
 * Extracted from SkillsTabPanel to isolate the modal rendering logic.
 *
 * Props:
 *   isOpen        {boolean}   Whether the modal is visible
 *   isSubmitting  {boolean}   Whether a submit operation is in progress (disables confirm button)
 *   header        {string}    Modal header text (e.g. "Add Technical Skill")
 *   name          {string}    Skill name to display (read-only)
 *   level         {number}    Current rating level (1-5)
 *   onRate        {Function}  Callback when rating changes. fn(e, { rating })
 *   onConfirm     {Function}  Callback when user confirms the add action
 *   onClose       {Function}  Callback to close the modal
 */
const SkillAddModal = ({
  isOpen,
  isSubmitting,
  header,
  name,
  level,
  onRate,
  onConfirm,
  onClose,
}) => {
  return (
    <Modal
      dimmer="blurring"
      open={isOpen}
      onClose={onClose}
      size="mini"
      closeIcon
    >
      <Modal.Header>{header}</Modal.Header>
      <Modal.Content>
        <Form>
          <Form.Input
            fluid
            label="Name"
            value={name}
            readOnly
            disabled
          />
          <Form.Field>
            <label htmlFor="skill-modal-rating">Level</label>
            <Rating
              id="skill-modal-rating"
              icon="star"
              rating={level}
              maxRating={5}
              size="huge"
              onRate={onRate}
              disabled={isSubmitting}
              aria-label="Skill level rating"
            />
          </Form.Field>
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          primary
          onClick={onConfirm}
          disabled={isSubmitting}
          loading={isSubmitting}
        >
          <Icon name="add" aria-hidden="true" /> Add
        </Button>
      </Modal.Actions>
    </Modal>
  )
}

SkillAddModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  header: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  level: PropTypes.number.isRequired,
  onRate: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

export default SkillAddModal
